import React, { useEffect, useState } from "react";
import { T } from "./Translate";
import {
  Container,
  Box,
  Typography,
  List,
  ListItem,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Chat as ChatIcon } from "../icons";
import { Download as DownloadIcon } from "../icons";
import { Visibility as VisibilityIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { Flight as FlightIcon } from "../icons";
import { Storefront as StorefrontIcon } from "../icons";
import { Event as EventIcon } from "../icons";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { collection, onSnapshot, query, where, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import PageHeader from "./PageHeader";
import BottomToolbar from "./BottomToolbar";
import { pageContentSx } from "../pageLayout";
import { brandColors } from "../theme";
import { downloadPurchaseReceipt } from "../utils/receipt";

const toJsDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (date.toDate instanceof Function) return date.toDate();
  return null;
};

const formatDate = (date) => {
  if (!date) return "";
  if (date instanceof Date) return date.toLocaleDateString();
  if (date.toDate instanceof Function) return date.toDate().toLocaleDateString();
  return "";
};

const sectionTitleSx = { fontWeight: "bold", mb: 1.5, display: "flex", alignItems: "center", gap: 1 };
const itemSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 0.5,
  py: 1.5,
  borderBottom: `1px solid ${brandColors.divider}`,
};

// Busca un campo de un doc referenciado (título de evento/auto/propiedad,
// nombre de vendedor) una sola vez por id, no en cada snapshot — mismo
// criterio para todas las secciones que necesitan resolver un id externo
// (ticketSales.eventId, vehiculos.autoId, trips.viajeId, sellerId de las 4).
const useReferencedField = (items, idField, collectionName, field = "title") => {
  const [values, setValues] = useState({});
  useEffect(() => {
    const missingIds = items
      .map((item) => item[idField])
      .filter((id) => id && !(id in values));
    if (!missingIds.length) return;

    missingIds.forEach(async (id) => {
      try {
        const snap = await getDoc(doc(db, collectionName, id));
        setValues((prev) => ({ ...prev, [id]: snap.exists() ? snap.data()[field] : null }));
      } catch (error) {
        console.error(`Error al obtener ${collectionName}/${id}:`, error);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  return values;
};

// "Mis compras" reemplaza las pestañas "Comprado"/"Reservados" que tenían
// CarsBottomNav/TripsBottomNav (autos y viajes), y suma acá mismo las
// compras en tiendas (sales) y entradas de eventos (ticketSales), que antes
// no tenían ningún historial visible para el comprador.
const MyPurchases = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [storePurchases, setStorePurchases] = useState([]);
  const [ticketPurchases, setTicketPurchases] = useState([]);
  const [offerPurchases, setOfferPurchases] = useState([]);
  const [vehiclePurchases, setVehiclePurchases] = useState([]);
  const [tripPurchases, setTripPurchases] = useState([]);
  const [buyerProfile, setBuyerProfile] = useState({ displayName: null, email: null });

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setLoading(false);
      return;
    }

    setBuyerProfile((prev) => ({ ...prev, email: auth.currentUser?.email || null }));
    getDoc(doc(db, "users", uid))
      .then((snap) => {
        if (snap.exists()) {
          setBuyerProfile((prev) => ({ ...prev, displayName: snap.data().displayName || null }));
        }
      })
      .catch((error) => console.error("Error al obtener el perfil del comprador:", error));

    const collections = [
      { name: "sales", setter: setStorePurchases },
      { name: "ticketSales", setter: setTicketPurchases },
      { name: "offerSales", setter: setOfferPurchases },
      { name: "vehiculos", setter: setVehiclePurchases },
      { name: "trips", setter: setTripPurchases },
    ];

    const unsubscribers = collections.map(({ name, setter }) =>
      onSnapshot(
        query(collection(db, name), where(["sales", "ticketSales", "offerSales"].includes(name) ? "buyerId" : "uid", "==", uid)),
        (snapshot) => {
          setter(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        () => setLoading(false)
      )
    );

    return () => unsubscribers.forEach((unsub) => unsub());
  }, []);

  const eventTitles = useReferencedField(ticketPurchases, "eventId", "events");
  const carTitles = useReferencedField(vehiclePurchases, "autoId", "cars");
  const placeTitles = useReferencedField(tripPurchases, "viajeId", "places");

  // Nombre del vendedor/anfitrión para el comprobante — todas las secciones
  // ya usan sellerId para el botón de chat, así que se resuelve una sola vez
  // por sellerId sin importar de qué sección viene.
  const allPurchases = [...vehiclePurchases, ...tripPurchases, ...storePurchases, ...ticketPurchases, ...offerPurchases];
  const sellerNames = useReferencedField(allPurchases, "sellerId", "users", "displayName");

  const downloadReceipt = (type, item, { itemTitle, amount, status, date, sellerName }) => {
    downloadPurchaseReceipt({
      type,
      id: item.id,
      itemTitle,
      amount,
      status,
      date,
      buyerName: buyerProfile.displayName,
      buyerEmail: buyerProfile.email,
      sellerName: sellerName || (item.sellerId ? sellerNames[item.sellerId] : null),
    });
  };

  // Auto/viaje: chatbox.js sabe resolver esos dos tipos (item + flujo de
  // pago/reserva de checkout.js), así que se navega igual que desde
  // CarDetail.js/PropertyDetail.js. Tienda/evento: chatbox.js NO tiene esos
  // tipos dados de alta (getItemCollectionName solo conoce "auto"/"viaje"/
  // "directo") y además siempre muestra el botón de pago por reserva, que no
  // aplica a una compra de tienda o una entrada ya pagada — por eso para
  // estos dos se usa un chat "directo" sin ítem asociado.
  const goToChat = (otherUserId, { type, itemId } = {}) => {
    if (!otherUserId) return;
    if (type === "auto" || type === "viaje") {
      navigate(`/chatbox/${type}/${itemId}/${otherUserId}`);
    } else {
      navigate(`/chatbox/directo/${otherUserId}`);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <PageHeader title={<T>Mis compras</T>} />

      <Container sx={{ mt: 5, mb: 12, maxWidth: { xs: "100%", md: "980px" } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            <DirectionsCarIcon /> <T>Autos</T>
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : vehiclePurchases.length > 0 ? (
            <List sx={{ p: 0 }}>
              {vehiclePurchases.map((vehicle) => (
                <ListItem key={vehicle.id} sx={itemSx} disableGutters>
                  <Typography sx={{ fontWeight: 500 }}>
                    {carTitles[vehicle.autoId] || <T>Auto</T>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${vehicle.paidAmount} {vehicle.paymentCompleted ? <T>(pagado)</T> : <T>(reservado)</T> } — {formatDate(vehicle.createdAt)}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/cars/${vehicle.autoId}`)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Ver auto</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => goToChat(vehicle.sellerId, { type: "auto", itemId: vehicle.autoId })}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Chatear con el vendedor</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        downloadReceipt("auto", vehicle, {
                          itemTitle: carTitles[vehicle.autoId] || "Auto",
                          amount: vehicle.paidAmount,
                          status: vehicle.paymentCompleted ? "Pagado" : "Reservado",
                          date: toJsDate(vehicle.createdAt),
                        })
                      }
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Descargar comprobante</T>
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <T>Todavía no compraste ni reservaste ningún auto.</T>
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            <FlightIcon /> <T>Viajes</T>
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : tripPurchases.length > 0 ? (
            <List sx={{ p: 0 }}>
              {tripPurchases.map((trip) => (
                <ListItem key={trip.id} sx={itemSx} disableGutters>
                  <Typography sx={{ fontWeight: 500 }}>
                    {placeTitles[trip.viajeId] || <T>Propiedad</T>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${trip.paidAmount} {trip.paymentCompleted ? <T>(pagado)</T> : <T>(reservado)</T>} — {formatDate(trip.createdAt)}
                  </Typography>
                  {trip.checkIn && (
                    <Typography variant="body2" color="text.secondary">
                      <T>Estadía:</T> {new Date(`${trip.checkIn}T12:00:00`).toLocaleDateString()} – {new Date(`${trip.checkOut}T12:00:00`).toLocaleDateString()}
                      {trip.remainingAmount > 0 && <> · <T>Resta pagar al anfitrión:</T> ${trip.remainingAmount}</>}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/trips/${trip.viajeId}`)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Ver propiedad</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => goToChat(trip.sellerId, { type: "viaje", itemId: trip.viajeId })}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Chatear con el vendedor</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        downloadReceipt("viaje", trip, {
                          itemTitle: placeTitles[trip.viajeId] || "Propiedad",
                          amount: trip.paidAmount,
                          status: trip.paymentCompleted ? "Pagado" : "Reservado",
                          date: toJsDate(trip.createdAt),
                        })
                      }
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Descargar comprobante</T>
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <T>Todavía no reservaste ningún viaje o propiedad.</T>
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            <StorefrontIcon /> <T>Tiendas</T>
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : storePurchases.length > 0 ? (
            <List sx={{ p: 0 }}>
              {storePurchases.map((sale) => (
                <ListItem key={sale.id} sx={itemSx} disableGutters>
                  <Typography sx={{ fontWeight: 500 }}>
                    {sale.productName || sale.productId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sale.storeName} — ${sale.totalAmount} — {formatDate(sale.purchaseDate)}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {sale.storeId && sale.productId && (
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => navigate(`/stores/${sale.storeId}/products/${sale.productId}`)}
                        sx={{ textTransform: "none", p: 0 }}
                      >
                        <T>Ver producto</T>
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => goToChat(sale.sellerId)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Chatear con la tienda</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        downloadReceipt("tienda", sale, {
                          itemTitle: sale.productName || sale.productId,
                          amount: sale.totalAmount,
                          status: "Pagado",
                          date: toJsDate(sale.purchaseDate),
                          sellerName: sale.storeName,
                        })
                      }
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Descargar comprobante</T>
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <T>Todavía no compraste nada en ninguna tienda.</T>
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            <LocalOfferIcon /> <T>Ofertas</T>
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : offerPurchases.length > 0 ? (
            <List sx={{ p: 0 }}>
              {offerPurchases.map((sale) => (
                <ListItem key={sale.id} sx={itemSx} disableGutters>
                  <Typography sx={{ fontWeight: 500 }}>
                    {sale.offerTitle || <T>Oferta</T>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${Number(sale.amount).toFixed(2)} — {formatDate(sale.purchaseDate)}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/offers/${sale.offerId}`)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Ver oferta</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => goToChat(sale.sellerId)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Acordar envío por chat</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        downloadReceipt("oferta", sale, {
                          itemTitle: sale.offerTitle || "Oferta",
                          amount: sale.amount,
                          status: "Pagado",
                          date: toJsDate(sale.purchaseDate),
                        })
                      }
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Descargar comprobante</T>
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <T>Todavía no compraste ninguna oferta.</T>
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={sectionTitleSx}>
            <EventIcon /> <T>Entradas de eventos</T>
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : ticketPurchases.length > 0 ? (
            <List sx={{ p: 0 }}>
              {ticketPurchases.map((ticket) => (
                <ListItem key={ticket.id} sx={itemSx} disableGutters>
                  <Typography sx={{ fontWeight: 500 }}>
                    {eventTitles[ticket.eventId] || <T>Evento</T>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ${ticket.amount} — {formatDate(ticket.purchaseDate)}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/events?highlight=${ticket.eventId}`)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Ver evento</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ChatIcon />}
                      onClick={() => goToChat(ticket.sellerId)}
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Chatear con el organizador</T>
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() =>
                        downloadReceipt("evento", ticket, {
                          itemTitle: eventTitles[ticket.eventId] || "Evento",
                          amount: ticket.amount,
                          status: "Pagado",
                          date: toJsDate(ticket.purchaseDate),
                        })
                      }
                      sx={{ textTransform: "none", p: 0 }}
                    >
                      <T>Descargar comprobante</T>
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              <T>Todavía no compraste entradas para ningún evento.</T>
            </Typography>
          )}
        </Box>
      </Container>

      <BottomToolbar />
    </Container>
  );
};

export default MyPurchases;
